import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import {
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Create as CreateIcon,
  TrendingUp as TrendingUpIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

const standalonePlans = [
  {
    id: '1',
    name: 'Enterprise',
    tierLabel: 'Tier 1',
    price: 3999,
    tierCopy: 'Tier 1 – 1 PR',
    bullets: [
      'Global, National & Regional coverage',
      'Advanced analytics',
      'Dedicated Acc. Manager',
      'Priority support',
    ],
  },
  {
    id: '2',
    name: 'Premium',
    tierLabel: 'Tier 2',
    price: 2999,
    tierCopy: 'Tier 2 – 1 PR',
    bullets: [
      'National & Regional coverage',
      'Advanced analytics',
      'Dedicated Acc. Manager',
      '24/7 Customer service',
    ],
  },
  {
    id: '3',
    name: 'Pro',
    tierLabel: 'Tier 3',
    price: 1999,
    tierCopy: 'Tier 3 – 1 PR',
    bullets: [
      'National & Regional coverage',
      'Advanced analytics',
      '24/7 Customer service',
    ],
  },
  {
    id: '4-5',
    name: 'Basic',
    tierLabel: 'Tier 4–5',
    price: 999,
    tierCopy: 'Tier 4 & 5 – 1 PR',
    bullets: [
      'National & Regional coverage',
      'Standard analytics',
      '24/7 Customer service',
    ],
  },
];

const packagePlans = [
  {
    id: 'pkg-1',
    name: 'Enterprise Package',
    tierLabel: 'Tier 1',
    price: 69999,
    credits: 20,
    tierCopy: 'Tier 1 – 20 PRs',
    bullets: [
      'Global, National, and Regional coverage',
      'Advanced analytics',
      'Dedicated Acc. Manager',
      'Priority support',
    ],
  },
  {
    id: 'pkg-2',
    name: 'Premium Package',
    tierLabel: 'Tier 2',
    price: 52999,
    credits: 20,
    tierCopy: 'Tier 2 – 20 PRs',
    bullets: [
      'National & Regional coverage',
      'Advanced analytics',
      'Dedicated Acc. Manager',
      '24/7 Customer service',
    ],
  },
  {
    id: 'pkg-3',
    name: 'Pro Package',
    tierLabel: 'Tier 3',
    price: 34999,
    credits: 20,
    tierCopy: 'Tier 3 – 20 PRs',
    bullets: [
      'National & Regional coverage',
      'Advanced analytics',
      '24/7 Customer service',
    ],
  },
  {
    id: 'pkg-4',
    name: 'Basic Package',
    tierLabel: 'Tier 4–5',
    price: 17999,
    credits: 20,
    tierCopy: 'Tier 4 & 5 – 20 PRs',
    bullets: [
      'National & Regional coverage',
      'Standard analytics',
      '24/7 Customer service',
    ],
  },
];

const tiers = [
  {
    id: '1',
    name: 'Tier 1',
    description: 'Major national and global outlets with the highest reach.',
  },
  {
    id: '2',
    name: 'Tier 2',
    description: 'Industry-specific publications and leading vertical media.',
  },
  {
    id: '3',
    name: 'Tier 3',
    description: 'Regional newspapers, TV, radio and online media.',
  },
  {
    id: '4',
    name: 'Tier 4',
    description: 'Niche blogs, portals and specialist communities.',
  },
  {
    id: '5',
    name: 'Tier 5',
    description: 'Trade journals and long-tail publications for SEO.',
  },
];

const formatPrice = (price: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(price);

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [pricingMode, setPricingMode] = useState<'standalone' | 'package'>('standalone');

  const handleModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'standalone' | 'package' | null,
  ) => {
    if (newMode !== null) {
      setPricingMode(newMode);
    }
  };

  const currentPlans = pricingMode === 'standalone' ? standalonePlans : packagePlans;
  const isStandalone = pricingMode === 'standalone';

  return (
    <Box>
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="lg">
          {/* Header with Toggle */}
          <Box
            sx={{
              textAlign: 'center',
              mb: { xs: 4, md: 5 },
            }}
          >
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, mb: 3, letterSpacing: '-0.03em' }}
            >
              Choose Your Plan
            </Typography>

            {/* Toggle Switch with Round Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  p: 0.5,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                {/* Round sliding indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: pricingMode === 'standalone' ? '4px' : '50%',
                    transform: 'translateY(-50%)',
                    width: 'calc(50% - 4px)',
                    height: 'calc(100% - 8px)',
                    bgcolor: 'primary.main',
                    borderRadius: 2.5,
                    transition: 'left 0.3s ease',
                    zIndex: 0,
                  }}
                />
                {/* Toggle buttons */}
                <ToggleButtonGroup
                  value={pricingMode}
                  exclusive
                  onChange={handleModeChange}
                  aria-label="pricing mode"
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    bgcolor: 'transparent',
                    '& .MuiToggleButton-root': {
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      border: 'none',
                      color: 'text.secondary',
                      bgcolor: 'transparent',
                      position: 'relative',
                      minWidth: 120,
                      '&:hover': {
                        bgcolor: 'transparent',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'transparent',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'transparent',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="standalone" aria-label="standalone">
                    Standalone
                  </ToggleButton>
                  <ToggleButton value="package" aria-label="package">
                    Package
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            <Typography
              variant="body1"
              sx={{ opacity: 0.9, maxWidth: 720, mx: 'auto', mb: 2 }}
            >
              {isStandalone
                ? 'Launch a single campaign whenever you need it. Simple, upfront pricing for one‑off PRs.'
                : 'Bundle your credits and run campaigns over the year with locked‑in pricing.'}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontStyle: 'italic',
                opacity: 0.95,
              }}
            >
              {isStandalone
                ? '*All pay per PRs should be exhausted within 30 days of its purchase.*'
                : '*All PRs should be exhausted within 365 days of its purchase.*'}
            </Typography>
          </Box>

          {/* Pricing Cards */}
          <Grid container spacing={3}>
            {currentPlans.map((plan) => {
              const pricePerCredit = isStandalone
                ? null
                : Math.round((plan as typeof packagePlans[0]).price / (plan as typeof packagePlans[0]).credits);
              return (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        variant="h6"
                        sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}
                      >
                        {isStandalone ? plan.name : plan.name.replace(' Package', '')}
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          textAlign: 'center',
                          fontWeight: 800,
                          mb: 1,
                          letterSpacing: '-0.04em',
                        }}
                      >
                        {formatPrice(plan.price)}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ textAlign: 'center', mb: isStandalone ? 2 : 0.5, fontWeight: 500 }}
                      >
                        {plan.tierCopy}
                      </Typography>
                      {!isStandalone && pricePerCredit && (
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: 'center',
                            mb: 2,
                            opacity: 0.85,
                          }}
                        >
                          National, regional & niche coverage across{' '}
                          {(plan as typeof packagePlans[0]).credits} PRs · approx.{' '}
                          {formatPrice(pricePerCredit)} per PR.
                        </Typography>
                      )}
                      <Stack
                        component="ul"
                        spacing={1}
                        sx={{
                          listStyle: 'none',
                          p: 0,
                          m: 0,
                          fontSize: '0.95rem',
                          flex: 1,
                        }}
                      >
                        {plan.bullets.map((item) => (
                          <Stack
                            key={item}
                            direction="row"
                            spacing={1.2}
                            alignItems="flex-start"
                            component="li"
                          >
                            <CheckCircleIcon fontSize="small" color="success" />
                            <Typography variant="body2">{item}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Corp Crunch Banner Card */}
          <Paper
            elevation={0}
            sx={{
              mt: 4,
              mb: 4,
              p: 4,
              borderRadius: 3,
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              position: 'relative'
            }}
          >
            <Grid container spacing={3} alignItems="center">
              {/* Left Side: Publication Info */}
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: '#09273b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <StarIcon sx={{ color: '#fff', fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#000' }}>
                        Corp Crunch
                      </Typography>
                      <Chip
                        label="EXCLUSIVE"
                        sx={{
                          backgroundColor: '#ff5200',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          height: 24,
                          px: 1
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                      Premium Tier 1 Publication • Skip the queue and publish directly
                    </Typography>
                  </Box>
                </Box>

                {/* Metrics */}
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#000', mb: 0.5 }}>
                      50M+
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.8rem' }}>
                      Monthly Reach
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#000', mb: 0.5 }}>
                      95 DA
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.8rem' }}>
                      Domain Authority
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#000', mb: 0.5 }}>
                      24 Hours
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.8rem' }}>
                      Publish Time
                    </Typography>
                  </Grid>
                </Grid>

                {/* Features */}
                <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                      Guaranteed Placement
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <TrendingUpIcon sx={{ fontSize: 18, color: '#ff5200' }} />
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                      SEO Optimized
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <EmailIcon sx={{ fontSize: 18, color: '#1a73e8' }} />
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                      Direct Contact
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Right Side: Pricing */}
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                  ONE-TIME PAYMENT
                </Typography>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#000' }}>
                  $1,499
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Call to Action */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Ready to get started?
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 4, opacity: 0.8, maxWidth: 600, mx: 'auto' }}
            >
              Create your first press release and start distributing to top publications today.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<CreateIcon />}
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/create-pr')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Create PR
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Tier explanation */}
        <Box
          sx={{
            py: 5,
            px: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            How tiers work
          </Typography>
          <Grid container spacing={2}>
            {tiers.map((tier) => (
              <Grid item xs={12} sm={6} md={4} key={tier.id}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <StarIcon
                    fontSize="small"
                    sx={{ mt: 0.3, color: 'warning.main' }}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.25 }}
                    >
                      {tier.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tier.description}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing;


